import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import logo from '../../../components/layout/logo_new.png';
import MessageHandler from '../../../components/core/MessageHandler';
import { setAuthToken } from '../../../services/getAuthToken';
import { requestEmailCode, verifyChallenge } from '../api/mfa.api';
import type { MfaSecondStepMode } from '../types/mfa.types';

interface RouterState {
  challengeToken?: string;
}

const isExpiredChallenge = (err: unknown) => {
  if (err instanceof AxiosError) {
    return err.response?.status === 409 || err.response?.status === 401 && /timed out|no longer required/i.test(String(err.response?.data?.message ?? ''));
  }
  return false;
};

// Mode -> translation key. Kept as keys rather than resolved strings so the
// maps can stay module-level while the copy still comes from i18n.
const codePlaceholderKey: Record<MfaSecondStepMode, string> = {
  totp: 'mfa.second_step_placeholder_totp',
  recovery: 'mfa.second_step_placeholder_recovery',
  email: 'mfa.second_step_placeholder_email',
};

const codeHelpKey: Record<MfaSecondStepMode, string> = {
  totp: 'mfa.second_step_help_totp',
  recovery: 'mfa.second_step_help_recovery',
  email: 'mfa.second_step_help_email',
};

const LoginSecondStep = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const challengeToken = (location.state as RouterState | null)?.challengeToken ?? '';

  const [mode, setMode] = useState<MfaSecondStepMode>('totp');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Defensive: if a user lands here directly (no router state), kick back to /login.
  if (!challengeToken) {
    navigate('/login', { replace: true });
    return null;
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!code.length) {
      setError(t('mfa.second_step_error_empty'));
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const session = await verifyChallenge(challengeToken, code.trim());
      await setAuthToken(session.accessToken);
      navigate('/');
    } catch (err) {
      if (isExpiredChallenge(err)) {
        setError(t('mfa.second_step_error_expired'));
        // Small delay so the message is visible before the redirect.
        window.setTimeout(() => navigate('/login', { replace: true }), 1500);
        return;
      }
      const message =
        err instanceof AxiosError
          ? err.response?.data?.message ?? t('mfa.second_step_error_bad_code')
          : t('mfa.second_step_error_bad_code');
      setError(String(message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestEmailCode = async () => {
    setError('');
    try {
      await requestEmailCode(challengeToken);
      setEmailSent(true);
      setMode('email');
      setCode('');
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 403) {
        setError(t('mfa.second_step_error_email_admin_only'));
        return;
      }
      if (isExpiredChallenge(err)) {
        setError(t('mfa.second_step_error_expired'));
        window.setTimeout(() => navigate('/login', { replace: true }), 1500);
        return;
      }
      const message =
        err instanceof AxiosError
          ? err.response?.data?.message ?? t('mfa.second_step_error_email_failed')
          : t('mfa.second_step_error_email_failed');
      setError(String(message));
    }
  };

  const switchMode = (next: MfaSecondStepMode) => {
    setMode(next);
    setCode('');
    setError('');
  };

  return (
    <div className="bg-login-background bg-cover">
      <div className="h-screen flex items-center justify-center">
        <form
          onSubmit={handleSubmit}
          className="bg-white border-grey-input_border border-2 shadow-lg rounded-2xl flex flex-col justify-around py-8 px-12 space-y-5 max-w-sm"
        >
          <img className="mx-auto p-6" src={logo} alt="School's Cool" />

          <div className="flex flex-col space-y-1">
            <h1 className="text-lg font-semibold text-grey-dark">
              {t('mfa.second_step_title')}
            </h1>
            <p className="text-sm text-grey-medium">{t(codeHelpKey[mode])}</p>
          </div>

          <div className="flex flex-col space-y-1">
            <label className="text-sm text-grey-medium">
              {t('mfa.second_step_code_label')}
            </label>
            <input
              type="text"
              inputMode={mode === 'totp' ? 'numeric' : 'text'}
              autoComplete="one-time-code"
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="placeholder-grey-medium text-sm rounded-lg shadow-md border-none py-2 px-3"
              placeholder={t(codePlaceholderKey[mode])}
            />
          </div>

          <button
            disabled={submitting || !code.length}
            type="submit"
            className="rounded-lg bg-sky text-white px-2 shadow-md py-1 hover:bg-sky-dark disabled:hover:bg-sky transition-colors"
          >
            {submitting
              ? t('mfa.second_step_verifying')
              : t('mfa.second_step_verify')}
          </button>

          <div className="flex flex-col space-y-2 text-sm">
            {mode !== 'recovery' && (
              <button
                type="button"
                onClick={() => switchMode('recovery')}
                className="text-sky hover:text-sky-dark text-left"
              >
                {t('mfa.second_step_use_recovery')}
              </button>
            )}
            {mode !== 'totp' && (
              <button
                type="button"
                onClick={() => switchMode('totp')}
                className="text-sky hover:text-sky-dark text-left"
              >
                {t('mfa.second_step_back_to_totp')}
              </button>
            )}
            {/* The "send me a code by email" path is admin-only on the server; */}
            {/* showing the button to everyone and surfacing the 403 reads cleaner */}
            {/* than client-side role-gating before the user knows what they need. */}
            {mode !== 'email' && (
              <button
                type="button"
                onClick={handleRequestEmailCode}
                className="text-sky hover:text-sky-dark text-left"
              >
                {t('mfa.second_step_email_code')}
              </button>
            )}
            {emailSent && mode === 'email' && (
              <p className="text-grey-medium">
                {t('mfa.second_step_email_sent')}
              </p>
            )}
          </div>

          <MessageHandler error={error} success="" />
        </form>
      </div>
    </div>
  );
};

export default LoginSecondStep;
